"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropTime from "./time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { ArtistSubmissionBadge } from "./ArtistSubmissionBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { ProfileWinnerBadge } from "./ProfileWinnerBadge";
import { useMemo, useCallback } from "react";
import { useArtistPreviewModal } from "@/hooks/useArtistPreviewModal";
import { useCompactMode } from "@/contexts/CompactModeContext";
interface WaveDropHeaderProps {
  readonly drop: ApiDrop;
  readonly isStorm: boolean;
  readonly currentPartIndex: number;
  readonly partsCount: number;
  readonly showWaveInfo: boolean;
  readonly badge?: React.ReactNode | undefined;
}
const WaveDropHeader: React.FC<WaveDropHeaderProps> = t0 => {
  const $ = _c(61);
  const {
    drop,
    isStorm,
    currentPartIndex,
    partsCount,
    showWaveInfo,
    badge
  } = t0;
  const router = useRouter();
  const compact = useCompactMode();
  const {
    isModalOpen,
    modalInitialTab,
    handleBadgeClick,
    handleModalClose
  } = useArtistPreviewModal();
  const submissionCount = drop.author.active_main_stage_submission_ids?.length || 0;
  const hasSubmissions = submissionCount > 0;
  const winnerCount = drop.author.winner_main_stage_drop_ids?.length || 0;
  const isWinner = winnerCount > 0;
  let t1;
  if ($[0] !== router) {
    t1 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleNavigation = t1;
  const t2 = `tw-mb-0 tw-leading-none tw-font-semibold ${compact ? "tw-text-sm" : "tw-text-md"}`;
  const t3 = drop.author.handle ?? drop.author.id;
  let t4;
  if ($[2] !== drop.author.handle || $[3] !== handleNavigation) {
    t4 = e_0 => handleNavigation(e_0, `/${drop.author.handle}`);
    $[2] = drop.author.handle;
    $[3] = handleNavigation;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `/${drop.author.handle}`;
  let t6;
  if ($[5] !== drop.author.handle || $[6] !== t4 || $[7] !== t5) {
    t6 = <Link onClick={t4} href={t5} className="tw-no-underline desktop-hover:hover:tw-underline tw-text-iron-200 desktop-hover:hover:tw-text-opacity-80 tw-transition tw-duration-300 tw-ease-out">{drop.author.handle}</Link>;
    $[5] = drop.author.handle;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t6) {
    t7 = <UserProfileTooltipWrapper user={t3}>{t6}</UserProfileTooltipWrapper>;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t2 || $[13] !== t7) {
    t8 = <p className={t2}>{t7}</p>;
    $[12] = t2;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== drop.author.level) {
    t9 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[15] = drop.author.level;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== drop.id || $[18] !== handleBadgeClick || $[19] !== hasSubmissions || $[20] !== submissionCount) {
    t10 = hasSubmissions && <ArtistSubmissionBadge submissionCount={submissionCount} onBadgeClick={() => handleBadgeClick("active")} tooltipId={`header-badge-${drop.id}`} />;
    $[17] = drop.id;
    $[18] = handleBadgeClick;
    $[19] = hasSubmissions;
    $[20] = submissionCount;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== drop.id || $[23] !== handleBadgeClick || $[24] !== isWinner || $[25] !== winnerCount) {
    t11 = isWinner && <ProfileWinnerBadge winCount={winnerCount} onBadgeClick={() => handleBadgeClick("winners")} tooltipId={`winner-badge-${drop.id}`} />;
    $[22] = drop.id;
    $[23] = handleBadgeClick;
    $[24] = isWinner;
    $[25] = winnerCount;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="tw-size-[3px] tw-bg-iron-600 tw-rounded-full tw-flex-shrink-0" />;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== drop.created_at) {
    t13 = <WaveDropTime timestamp={drop.created_at} />;
    $[28] = drop.created_at;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t13 || $[33] !== t8 || $[34] !== t9) {
    t14 = <div className="tw-flex tw-items-center tw-gap-x-2">{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t13;
    $[33] = t8;
    $[34] = t9;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== badge) {
    t15 = badge && <div className="tw-ml-2">{badge}</div>;
    $[36] = badge;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2"><div className="tw-flex tw-items-center tw-gap-x-2">{t14}{t15}</div></div>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== drop.wave || $[42] !== handleNavigation || $[43] !== showWaveInfo) {
    t17 = showWaveInfo && drop.wave && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      })?.chat;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage: waveMeta?.scope?.group?.is_direct_message ?? false,
        isApp: false
      });
      return <Link onClick={e_1 => handleNavigation(e_1, waveHref)} href={waveHref} className="tw-mb-0 tw-text-[11px] tw-leading-0 -tw-mt-1 tw-text-iron-500 hover:tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out tw-no-underline">{drop.wave.name}</Link>;
    })();
    $[41] = drop.wave;
    $[42] = handleNavigation;
    $[43] = showWaveInfo;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t17) {
    t18 = <div>{t17}</div>;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== currentPartIndex || $[48] !== isStorm || $[49] !== partsCount) {
    t19 = isStorm && <div className="tw-inline-flex tw-relative tw-mt-2"><span className="tw-text-xs tw-text-iron-50 tw-mb-1.5">{currentPartIndex + 1} /{" "}<span className="tw-text-iron-400">{partsCount}</span></span></div>;
    $[47] = currentPartIndex;
    $[48] = isStorm;
    $[49] = partsCount;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== drop.author || $[52] !== handleModalClose || $[53] !== isModalOpen || $[54] !== modalInitialTab) {
    t20 = <ArtistPreviewModal isOpen={isModalOpen} onClose={handleModalClose} user={drop.author} initialTab={modalInitialTab} />;
    $[51] = drop.author;
    $[52] = handleModalClose;
    $[53] = isModalOpen;
    $[54] = modalInitialTab;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== t16 || $[57] !== t18 || $[58] !== t19 || $[59] !== t20) {
    t21 = <>{t16}{t18}{t19}{t20}</>;
    $[56] = t16;
    $[57] = t18;
    $[58] = t19;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  return t21;
};
export default WaveDropHeader;